<script setup lang="ts">
  import { Typo, Icon } from '@travelata-libs/ui-kit';
  import type { AccountUser, DestinationPrices, SwitchCity } from 'src';
  import { computed, onMounted, ref } from 'vue';
  import HistorySearchCard from './HistorySearchCard.vue';
  import useFormModel from '../lib/useFormModel';
  import tabs from '../model/tabs';
  import { type TabType } from '../model/types';
  import { getToursUrl, getHotToursUrl, getHotelOffersUrl, getHistorySearch } from '@/entities/searchCriteria';
  import { LeadBanner } from '@/features/leadBanner';
  import { OfficeTourButton } from '@/features/officeTour';
  import { SwitchCityField } from '@/features/switchCity';
  import { FormDataUrl } from '@/shared/config/formData';
  import { LocalStorageKey } from '@/shared/config/localStorage';
  import { PagesUrl } from '@/shared/config/pages';
  import type { SearchCriteriaParams } from '@/shared/model';
  import SkolkovoLogo from '@/shared/ui/logos/SkolkovoLogo.vue';
  import TravelataLogo from '@/shared/ui/logos/TravelataLogo.vue';
  import { SearchForm } from '@/widgets/searchForm';
  import { INDEX_MORE_NAV_LINKS, INDEX_PRIMARY_NAV_LINKS, SiteHeader } from '@/widgets/siteHeader';

  interface IndexProps {
    prices?: DestinationPrices;
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    user?: AccountUser | null;
    accountUrl?: string;
    isVisibleRoomButton?: boolean;
  }

  withDefaults(defineProps<IndexProps>(), {
    prices: undefined,
    isVisibleRoomButton: true,
  });

  interface IndexEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'change:city', data: SwitchCity): void;
    (e: 'buy'): void;
  }
  const emit = defineEmits<IndexEmits>();
  const activeTab = ref<TabType>('tours');
  const historySearch = ref<SearchCriteriaParams[]>([]);

  const isToursOpen = computed(() => activeTab.value === 'tours');
  const isHotOpen = computed(() => activeTab.value === 'tours-hot');
  const isHotelsOpen = computed(() => activeTab.value === 'hotels');
  const isRailwayOpen = computed(() => activeTab.value === 'railway');

  const toursModel = useFormModel({
    withPrices: true,
    withHistory: true,
    lastCriteriaKey: LocalStorageKey.TourCriteria,
    isOpen: isToursOpen,
    to: PagesUrl.SerpTours,
    formUrl: FormDataUrl.Tours,
    getUrl: getToursUrl,
  });
  const hotModel = useFormModel({
    lastCriteriaKey: LocalStorageKey.TourCriteriaTury,
    isOpen: isHotOpen,
    to: PagesUrl.SerpHot,
    formUrl: FormDataUrl.Hot,
    getUrl: getHotToursUrl,
  });
  const hotelsModel = useFormModel({
    lastCriteriaKey: LocalStorageKey.TourCriteriaHotelOffers,
    isOpen: isHotelsOpen,
    to: PagesUrl.SerpHotels,
    formUrl: FormDataUrl.Hotels,
    getUrl: getHotelOffersUrl,
  });
  const railwayModel = useFormModel({
    lastCriteriaKey: LocalStorageKey.TourCriteriaRailwayTours,
    isOpen: isRailwayOpen,
    to: PagesUrl.SerpRailway,
    formUrl: FormDataUrl.Railway,
    getUrl: getToursUrl,
  });

  const currentTab = computed(() => tabs.find(({ type }) => type === activeTab.value));

  const onClickTab = (type: TabType) => {
    activeTab.value = type;
  };

  onMounted(async () => {
    const historySearchData = await getHistorySearch(true);
    historySearch.value = historySearchData;
  });
</script>

<template>
  <div
    v-if="currentTab"
    :class="['index-page', `index-page_background-${activeTab}`]"
  >
    <div>
      <SiteHeader
        :user="user"
        :visible-logos="false"
        :account-url="accountUrl"
        :should-adaptive="false"
        :button-bordered="false"
        :primary="INDEX_PRIMARY_NAV_LINKS"
        :more="INDEX_MORE_NAV_LINKS"
        class="index-page__header"
        @login="emit('login')"
        @logout="emit('logout')"
      >
        <template #action>
          <SwitchCityField
            :current-city="currentCity"
            :cities="cities"
            :redirect="false"
            class="index-page__switch-city"
            @change="emit('change:city', $event)"
          />
        </template>
      </SiteHeader>
    </div>
    <div class="index-page__container">
      <div class="index-page__main">
        <div class="index-page__logos">
          <TravelataLogo class="index-page__travelata-logo" />
          <div class="index-page__up-right">
            <SkolkovoLogo class="index-page__skolkovo-logo" />
            <OfficeTourButton
              size="l"
              class="index-page__office-tour"
              @buy="emit('buy')"
            />
          </div>
        </div>
      </div>

      <div class="index-page__text-container">
        <Typo
          class="index-page__text"
          variant="h-1"
        >
          {{ currentTab.title }}
          <br />
          или поручите подбор нашему эксперту
        </Typo>
      </div>
      <div class="index-page__tabs">
        <div
          v-for="tab in tabs"
          :key="tab.type"
          :class="[
            'index-page__tab',
            {
              'index-page__tab_active': activeTab === tab.type,
            },
          ]"
          @click="onClickTab(tab.type)"
        >
          <div class="index-page__tab-circle">
            <Icon
              size="xl"
              class="index-page__tab-icon"
              :name="tab.icon"
            />
            <div
              v-if="tab.type === 'tours-hot'"
              class="index-page__tab-discount"
            >
              <Typo
                variant="body-m-700-short"
                class="index-page__tab-discount-typo"
              >
                -50%
              </Typo>
            </div>
          </div>
          <Typo
            class="index-page__tab-typo"
            variant="body-l-400"
          >
            {{ tab.text }}
          </Typo>
        </div>
      </div>
      <SearchForm
        v-if="isToursOpen"
        :key="`${isToursOpen}_tours`"
        v-model="toursModel.model.value"
        :is-visible-room-button="isVisibleRoomButton"
        :prices="toursModel.prices.value"
        :form-url="toursModel.formUrl"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="toursModel.onSearch"
      />
      <SearchForm
        v-if="isHotOpen"
        :key="`${isHotOpen}_hot`"
        v-model="hotModel.model.value"
        :is-visible-room-button="isVisibleRoomButton"
        :form-url="hotModel.formUrl"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="hotModel.onSearch"
      />
      <SearchForm
        v-if="isHotelsOpen"
        :key="`${isHotelsOpen}_hotels`"
        v-model="hotelsModel.model.value"
        :is-visible-departure="false"
        :form-url="hotelsModel.formUrl"
        :is-visible-room-button="false"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="hotelsModel.onSearch"
      />
      <SearchForm
        v-if="isRailwayOpen"
        :key="`${isRailwayOpen}_railway`"
        v-model="railwayModel.model.value"
        :form-url="railwayModel.formUrl"
        :is-visible-room-button="false"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="railwayModel.onSearch"
      />

      <div class="index-page__lead-banner-container">
        <LeadBanner
          :title="currentTab.leadTitle"
          :subtitle="currentTab.leadSubtitle"
          :modal-title="currentTab.modalTitle"
          class="index-page__lead-banner"
        />
      </div>

      <div
        v-if="historySearch.length"
        class="index-page__history"
      >
        <Typo
          class="index-page__history-title"
          variant="body-l-500"
        >
          Вы искали в прошлый раз
        </Typo>
        <div class="index-page__history-container">
          <HistorySearchCard
            v-for="(value, index) in historySearch"
            :key="index"
            :history-search="value"
            :is-first="index === 0"
            :is-last="index === historySearch.length - 1"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @function index-background-gradient($color-1, $color-2, $color-3) {
    @return linear-gradient(280deg, $color-1 -10.88%, $color-2 24.67%, $color-3 100.45%);
  }

  .index-page {
    $root: &;

    @keyframes tab-scale {
      0% {
        transform: scale(1);
      }

      35% {
        transform: scale(0.97);
      }

      70% {
        transform: scale(1.02);
      }

      100% {
        transform: scale(1);
      }
    }

    @include responsive-max-width('&__lead-banner-container');
    @include responsive-max-width('&__logos');
    @include responsive-max-width('&__history');

    &__search-form {
      background: transparent;
      margin-bottom: var(--spacing-xxl);
      padding-bottom: 0;
    }

    &__office-tour {
      height: 48px;
    }

    &_background-tours {
      background: index-background-gradient(var(--color-cyan-40), var(--color-brand-secondary), var(--color-sky-50));
    }

    &_background-tours-hot {
      background: index-background-gradient(var(--color-purple-30), var(--color-purple-40), var(--color-purple-60));
    }

    &_background-hotels {
      background: index-background-gradient(var(--color-cyan-40), var(--color-cyan-50), var(--color-cyan-50));
    }

    &_background-railway {
      background: linear-gradient(280deg, var(--color-green-20) -11.48%, var(--color-green-30) 2.15%, var(--color-green-50) 96.97%);
    }

    &__switch-city {
      margin-right: var(--spacing-xxs);
    }

    &__up-right {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxl);
    }

    &__container {
      margin: 0 auto;
      max-width: $max-width-xl;
    }

    &__text-container {
      margin: 0 auto;
      margin-bottom: var(--spacing-xxl);
      max-width: 786px;
    }

    &__text {
      color: var(--color-text-inverse);
      text-align: center;
    }

    &__lead-banner-container {
      margin-left: auto;
      margin-right: auto;
    }

    &__lead-banner {
      backdrop-filter: blur(14.6px);
      background: rgba(255, 255, 255, 0.15);
      border-color: var(--color-text-inverse);
      border-radius: var(--rounding-xl);
      box-shadow: var(--shadow-xxs);
      color: var(--color-text-inverse);
      margin: auto;
      max-width: 867px;
      box-sizing: border-box;
    }

    &__skolkovo-logo {
      height: 36px;
      width: 122px;
    }

    &__travelata-logo {
      height: 48px;
      width: 205px;
    }

    &__tab-circle {
      align-items: center;
      background: rgb(255 255 255 / 0.33);
      border-radius: 100%;
      display: flex;
      height: 60px;
      justify-content: center;
      width: 60px;
    }

    &__tab-icon {
      fill: var(--color-text-inverse);
    }

    &__tab {
      align-items: center;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      position: relative;
      transition: transform 0.2s cubic-bezier(0.33, 1, 0.68, 1);

      &:not(#{$root}__tab_active):hover {
        transform: scale(1.04);
      }

      &_active {
        animation: tab-scale 0.35s cubic-bezier(0.33, 1, 0.68, 1);
        #{$root}__tab-circle {
          background-color: var(--color-text-inverse);
        }
        #{$root}__tab-icon {
          fill: var(--color-text-primary);
        }
      }
    }

    &__tab-discount-typo {
      line-height: normal;
    }

    &__tab-discount {
      align-items: center;
      background-color: var(--color-red-50);
      border-radius: var(--rounding-l);
      color: var(--color-text-inverse);
      display: flex;
      height: 20px;
      justify-content: center;
      left: -5px;
      position: absolute;
      top: -11px;
      transform: rotate(-8deg);
      width: 42px;
    }

    &__history-title {
      color: var(--color-text-inverse);
    }

    &__tabs {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
      justify-content: center;
      margin: 0 auto;
    }

    &__tab-typo {
      color: var(--color-text-inverse);
    }

    &__header {
      background: var(--color-text-primary);
      padding-bottom: var(--spacing-s);
      position: relative;
      height: 48px;
      z-index: 5;
    }

    &__logos {
      display: flex;
      height: 48px;
      justify-content: space-between;
      margin: var(--spacing-xl) auto;
      width: 100%;
    }

    &__history {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      margin: 0 auto;
      margin-top: var(--spacing-xxxl);
      max-width: $max-width-xl;
    }

    &__history-container {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-s);
    }
  }
</style>
